<template>
  <div class="smst">

    <b-table
        ref="feature-table"
        responsive
        v-bind:items="messages"
        v-bind:fields="fields"
        :busy="loading"
        class="smst-table"
    >
      <template #table-busy>
        <div class="text-center text-primary my-3">
          <b-spinner class="align-middle mx-1"></b-spinner>
          <strong>{{ msg('Loading') }}...</strong>
        </div>
      </template>

      <!-- direction: arrow squircle + label -->
      <template #cell(direction)="data">
        <div class="smst-dir">
          <span class="smst-dir__icon" :class="isInbound(data.value) ? 'smst-dir__icon--in' : 'smst-dir__icon--out'">
            <i :class="isInbound(data.value) ? 'fas fa-arrow-down' : 'fas fa-arrow-up'"/>
          </span>
          <span class="smst-dir__label">{{ isInbound(data.value) ? msg('inbound') : msg('outbound') }}</span>
        </div>
      </template>

      <!-- sender -->
      <template #cell(from)="data">
        <span class="smst-phone tabular">{{ data.value }}</span>
      </template>

      <!-- receiver: client name when the response's clients map provides one -->
      <template #cell(to)="data">
        <div v-if="clients[data.value] != null" class="smst-client">
          <button type="button" class="smst-client__name" @click="onClientSelect(clients[data.value])">
            {{ clients[data.value].clientFullname }}
          </button>
          <span class="smst-phone smst-client__phone tabular">{{ data.value }}</span>
        </div>
        <span v-else class="smst-phone tabular">{{ data.value }}</span>
      </template>

      <!-- body: 2-line clamp + tooltip, media count -->
      <template #cell(body)="data">
        <div class="smst-body">
          <p v-if="data.value" class="smst-body__text" v-b-tooltip.hover.top :title="data.value">{{ data.value }}</p>
          <span v-else class="smst-muted">&mdash;</span>
          <span v-if="mediaCount(data.item) > 0" class="smst-media tabular">
            <i class="fas fa-paperclip mr-25"/>{{ mediaCount(data.item) }}
          </span>
        </div>
      </template>

      <!-- status pill + error details for failed messages -->
      <template #cell(status)="data">
        <div class="smst-state">
          <span class="smst-pill" :class="`smst-pill--${statusTone(data.value)}`">
            <i :class="statusIcon(data.value)" class="mr-25"/>{{ msg(data.value) }}
          </span>
          <i v-if="hasError(data.item)" class="fas fa-circle-exclamation smst-error"
             v-b-tooltip.hover :title="errorTitle(data.item)"/>
        </div>
      </template>

      <!-- sent date (falls back to dateCreated for queued/failed messages) -->
      <template #cell(dateSent)="data">
        <div class="smst-dates" v-if="sentDate(data.item) != null">
          <span class="smst-dates__main tabular">{{ $moment(sentDate(data.item)).format('DD.MM.YY HH:mm') }}</span>
          <span class="smst-dates__sub">{{ timeAgo(sentDate(data.item)) }}</span>
        </div>
        <span v-else class="smst-muted">&mdash;</span>
      </template>

      <!-- price -->
      <template #cell(price)="data">
        <span v-if="data.value != null" class="smst-price tabular">
          {{ convertToEURO(data.value * (-1)) }}
        </span>
        <span v-else class="smst-muted">&mdash;</span>
      </template>

      <!-- segments -->
      <template #cell(numSegments)="data">
        <span class="smst-segments tabular">{{ data.value }}</span>
      </template>

    </b-table>

    <!-- empty state -->
    <div v-if="!loading && messages.length === 0" class="smst-empty">
      <i class="fas fa-message"/>
      <p>{{ msg('nodatafound') }}</p>
    </div>

    <!-- cursor pagination (Twilio next/previous page urls) -->
    <div class="smst-foot">
      <button
          type="button"
          class="smst-page-btn"
          :disabled="page.previousPageUrl == null || loading"
          @click="goToPreviousPage"
      >
        <i class="fas fa-chevron-left mr-50"/>{{ msg('Previous') }}
      </button>

      <button
          type="button"
          class="smst-page-btn"
          :disabled="page.nextPageUrl == null || loading"
          @click="goToNextPage"
      >
        {{ msg('Next') }}<i class="fas fa-chevron-right ml-50"/>
      </button>
    </div>

  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";

export default {
  name: "MessagesTable",
  components: {},
  props: ["filter"],
  watch: {
    filter: {
      deep: true,
      handler() {
        this.refresh();
      },
      immediate: true
    },
  },
  data: () => ({
    loading: true,
    clients: {},
    messages: [],
    dialogs: {
      client: false,
    },
    selected: null,

    page: {
      previousPageUrl: null,
      nextPageUrl: null,
    },
  }),
  computed: {
    ...mapGetters('data', ['getAllBranches']),
    allBranches() {
      return Object.values(this.getAllBranches)
    },
    fields() {
      let $this = this
      return [
        {
          key: 'direction',
          label: $this.msg('direction'),
          sortable: false,
          thClass: 'smst-th',
          tdClass: 'smst-td'
        },
        {
          key: 'from',
          label: $this.msg('sender'),
          sortable: true,
          thClass: 'smst-th',
          tdClass: 'smst-td'
        },
        {
          key: 'to',
          label: $this.msg('receiver'),
          sortable: true,
          thClass: 'smst-th',
          tdClass: 'smst-td'
        },
        {
          key: 'body',
          label: $this.msg('text'),
          sortable: true,
          thClass: 'smst-th',
          tdClass: 'smst-td'
        },
        {
          key: 'status',
          label: $this.msg('status'),
          sortable: true,
          thClass: 'smst-th',
          tdClass: 'smst-td'
        },
        {
          key: 'dateSent',
          label: $this.msg('sent'),
          sortable: true,
          thClass: 'smst-th',
          tdClass: 'smst-td'
        },
        {
          key: 'price',
          label: $this.msg('price'),
          sortable: true,
          thClass: 'smst-th text-right',
          tdClass: 'smst-td text-right'
        },
        {
          key: 'numSegments',
          label: $this.msg('segments'),
          sortable: true,
          thClass: 'smst-th text-center',
          tdClass: 'smst-td text-center'
        }
      ]
    },
  },
  methods: {
    ...mapActions('reception', ['fetchAllMessages']),
    convertToEURO(someNumber) {
      return new Intl.NumberFormat("de-AT", {
        style: "currency",
        currency: "EUR",
      }).format(someNumber);
    },
    goToNextPage() {
      this.filter.pageUrl = this.page.nextPageUrl;
      this.refresh();
    },
    goToPreviousPage() {
      this.filter.pageUrl = this.page.previousPageUrl;
      this.refresh();
    },
    isInbound(direction) {
      return String(direction || '').toLowerCase().indexOf('inbound') === 0;
    },
    statusTone(status) {
      switch (status) {
        case 'delivered':
        case 'sent':
        case 'received':
        case 'receiving':
          return 'ok'
        case 'undelivered':
          return 'warn'
        case 'failed':
          return 'fail'
        default: // queued, accepted, sending, scheduled, ...
          return 'queue'
      }
    },
    statusIcon(status) {
      switch (this.statusTone(status)) {
        case 'ok':
          return 'fas fa-check'
        case 'warn':
          return 'fas fa-exclamation'
        case 'fail':
          return 'fas fa-times'
        default:
          return 'far fa-clock'
      }
    },
    hasError(item) {
      return item != null && (item.errorCode != null || (item.errorMessage != null && item.errorMessage !== ''));
    },
    errorTitle(item) {
      let parts = [];
      if (item.errorCode != null) {
        parts.push(item.errorCode);
      }
      if (item.errorMessage != null && item.errorMessage !== '') {
        parts.push(item.errorMessage);
      }
      return parts.join(' - ');
    },
    mediaCount(item) {
      let count = parseInt(item != null ? item.numMedia : 0, 10);
      return isNaN(count) ? 0 : count;
    },
    sentDate(item) {
      if (item == null) {
        return null;
      }
      return item.dateSent != null ? item.dateSent : item.dateCreated;
    },
    timeAgo(date) {
      let momentValue = this.$moment(date);
      if (!momentValue.isValid()) {
        return '';
      }
      return momentValue.fromNow();
    },
    refresh() {
      let $this = this;
      this.loading = true;
      this.$emit('busy', true);
      this.fetchData().finally(() => {
        $this.loading = false;
        $this.$emit('busy', false);
      });
    },

    onClientSelect(client) {
      this.selected = client.id
      this.dialogs.client = true
      this.$root.$emit('bv::hide::popover')
      this.$root.$emit('crm::client::select', client.id)

    },

    fetchData() {
      let $this = this;

      return this.fetchAllMessages({
        filter: this.filter
      }).then(data => {
        $this.messages = data.messages || [];

        // Store the cursor URLs for Next/Previous
        $this.page.nextPageUrl = data.nextPageUrl || null;
        $this.page.previousPageUrl = data.previousPageUrl || null;

        // Convert clients to map by phone number
        $this.clients = (data.clients || []).toObject("phone");
        $this.$emit('count', $this.messages.length);
      });
    }

  }
}
</script>


<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

.smst-table { font-size: .85rem; }
.smst-table :deep(.smst-th) {
  font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em;
  color: #94a3b8; background: rgba(15, 23, 42, .02); border-bottom: 1px solid rgba(15, 23, 42, .07) !important;
}
.smst-table :deep(.smst-td) { vertical-align: middle; padding: 10px 12px; border-top: 1px solid rgba(15, 23, 42, .05); }
.smst-table :deep(tbody tr:hover) { background: rgba(115, 103, 240, .04); }

/* direction */
.smst-dir { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.smst-dir__icon {
  width: 26px; height: 26px; border-radius: 8px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; font-size: .68rem;
  &--in { background: rgba(40, 199, 111, .13); color: #1f9d57; }
  &--out { background: rgba(115, 103, 240, .12); color: #7367f0; }
}
.smst-dir__label {
  font-size: .64rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #94a3b8;
}

/* phones */
.smst-phone { white-space: nowrap; color: #475569; font-size: .8rem; }

/* client (receiver) */
.smst-client { display: flex; flex-direction: column; white-space: nowrap; }
.smst-client__name {
  border: none; background: none; padding: 0; cursor: pointer; text-align: inherit;
  font-weight: 700; color: #7367f0; font-size: .82rem; text-transform: capitalize;
  &:hover { text-decoration: underline; }
  &:focus { outline: none; }
}
.smst-client__phone { font-size: .68rem; color: #94a3b8; margin-top: 1px; }

/* body clamp */
.smst-body { display: flex; align-items: center; gap: 8px; min-width: 200px; max-width: 340px; }
.smst-body__text {
  margin: 0; color: #475569; font-size: .8rem; line-height: 1.35; cursor: default;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.smst-media {
  display: inline-flex; align-items: center; flex-shrink: 0;
  font-size: .66rem; font-weight: 800; color: #0090a5;
  background: rgba(0, 207, 232, .12); border-radius: 999px; padding: 3px 8px;
}
.smst-muted { color: #cbd5e1; }

/* status pills */
.smst-state { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.smst-pill {
  display: inline-flex; align-items: center;
  font-size: .66rem; font-weight: 800; letter-spacing: .03em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 999px; white-space: nowrap;
  &--ok { background: rgba(40, 199, 111, .13); color: #1f9d57; }
  &--warn { background: rgba(255, 159, 67, .15); color: #b26a12; }
  &--fail { background: rgba(234, 84, 85, .13); color: #c62a2b; }
  &--queue { background: rgba(100, 116, 139, .12); color: #64748b; }
}
.smst-error { color: #ea5455; cursor: help; font-size: .8rem; }

/* dates */
.smst-dates { display: flex; flex-direction: column; white-space: nowrap; }
.smst-dates__main { font-weight: 700; color: #1f2a44; font-size: .8rem; }
.smst-dates__sub { font-size: .66rem; color: #94a3b8; margin-top: 2px; }

/* price & segments */
.smst-price { font-weight: 800; color: #1f2a44; white-space: nowrap; }
.smst-segments {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 24px; height: 22px; padding: 0 7px; border-radius: 999px;
  background: rgba(15, 23, 42, .05); color: #64748b; font-size: .7rem; font-weight: 800;
}

/* empty + footer */
.smst-empty { text-align: center; padding: 40px 0 26px; color: #94a3b8; i { font-size: 2rem; opacity: .4; } p { margin-top: 8px; font-size: .85rem; } }
.smst-foot {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding-top: 12px; border-top: 1px solid rgba(15, 23, 42, .06);
}
.smst-page-btn {
  display: inline-flex; align-items: center;
  border: 1px solid rgba(15, 23, 42, .1); border-radius: 999px; background: transparent;
  color: #64748b; font-size: .76rem; font-weight: 700; cursor: pointer;
  padding: 6px 16px; transition: all .15s;
  &:hover:not(:disabled) { border-color: #7367f0; color: #7367f0; background: rgba(115, 103, 240, .08); }
  &:disabled { opacity: .45; cursor: default; }
  &:focus { outline: none; }
}
</style>

<style lang="scss">
.dark-layout {
  .smst-table .smst-th { background: rgba(255, 255, 255, .03); color: #8a92a6; border-color: rgba(255, 255, 255, .08) !important; }
  .smst-table .smst-td { border-color: rgba(255, 255, 255, .06); }
  .smst-table tbody tr:hover { background: rgba(115, 103, 240, .1); }
  .smst-phone { color: #b4b7bd; }
  .smst-body__text { color: #b4b7bd; }
  .smst-dates__main, .smst-price { color: #e9ecf2; }
  .smst-muted { color: #4b5261; }
  .smst-segments { background: rgba(255, 255, 255, .07); color: #94a3b8; }
  .smst-foot { border-color: rgba(255, 255, 255, .08); }
  .smst-page-btn { border-color: rgba(255, 255, 255, .16); color: #94a3b8; }
}
</style>
